import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Container,
  Fade,
  Grow,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Tabs,
  Tab,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import WorkIcon from '@mui/icons-material/Work';
import GroupsIcon from '@mui/icons-material/Groups';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import { GET, POST, PATCH, DELETE, removeLocalAccessTokenAndUser } from '../../configs/api';
import { api } from '../../utils/constants';
import { getUser, removeUser } from '../../store/slices/userSlice';
import { setSelectedCompany, clearSelectedCompany, getSelectedCompanyId } from '../../store/slices/tenantSlice';
import { toggleTheme } from '../../store/slices/themeSlice';
import { LoadScript } from '@react-google-maps/api';
import CompanyProfileSection from '../../components/company/CompanyProfileSection';
import CompanyPickerAmbientBg, { useCompanyPickerSurfaces } from './CompanyPickerAmbientBg';
import UserAvatar from '../../components/UserAvatar';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const COMPANY_ROLE_OPTIONS = [
  'owner',
  'general_manager',
  'accountant',
  'admin',
  'dispatcher',
];

const emptyMemberFormErrors = {
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  password: '',
  phone: '',
  roles: '',
};

const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  }
  if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  }
  if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

const validateNewMemberForm = (form) => {
  const newErrors = {};
  const firstName = form.first_name?.trim() || '';
  const lastName = form.last_name?.trim() || '';
  const email = form.email?.trim() || '';
  const username = form.username?.trim() || '';
  const password = form.password || '';

  if (firstName.length < 2 || firstName.length > 50) {
    newErrors.first_name = 'First name must be between 2 and 50 characters';
  }
  if (lastName.length < 2 || lastName.length > 50) {
    newErrors.last_name = 'Last name must be between 2 and 50 characters';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    newErrors.email = 'Please enter a valid email address';
  }

  const usernameRegex = /^[a-zA-Z0-9_]+$/;
  if (username.length < 3 || username.length > 50) {
    newErrors.username = 'Username must be between 3 and 50 characters';
  } else if (!usernameRegex.test(username)) {
    newErrors.username = 'Username must contain only alphanumeric characters and underscores';
  }

  if (password.length < 6 || password.length > 100) {
    newErrors.password = 'Password must be between 6 and 100 characters';
  }

  if (form.phone) {
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit US phone number';
    }
  }

  if (!Array.isArray(form.roles) || form.roles.length === 0) {
    newErrors.roles = 'At least one role is required';
  }

  return newErrors;
};

const normalizeCompanies = (companies) => {
  if (!Array.isArray(companies)) return [];
  return companies.map((c) => ({
    ...c,
    roles: Array.isArray(c.roles) ? c.roles : [],
  }));
};

/** Case-insensitive match: every whitespace-separated token must appear in name/email/username. */
const matchesUserTextFilter = (user, query) => {
  const tokens = String(query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;
  const haystack = [
    user.first_name,
    user.last_name,
    user.email,
    user.username,
  ].filter(Boolean).join(' ').toLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

/** Shared user info block: identity, status chips, multi-company memberships. */
const MemberInfoBox = ({
  member,
  showCurrentCompanyRoles = true,
  companyIdForMembershipChip = null,
}) => {
  const companies = normalizeCompanies(member.companies);
  const membershipInactive = companyIdForMembershipChip
    ? (companies.find((c) => c.id === companyIdForMembershipChip)?.is_active === false
      || member.membership_is_active === false)
    : (member.membership_is_active === false
      || companies.some((c) => c.is_active === false));
  const accountInactive = member.user_is_active === false;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1 }}>
      <UserAvatar
        user={member}
        size={44}
        sx={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: 'white',
          mt: 0.25,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>
          {member.first_name} {member.last_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {member.email} · @{member.username}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
          {showCurrentCompanyRoles && (member.roles || []).map((r) => (
            <Chip key={r} label={r} size="small" />
          ))}
          {membershipInactive && (
            <Chip label="inactive in company" size="small" color="warning" />
          )}
          {accountInactive && (
            <Chip label="account inactive" size="small" color="error" />
          )}
        </Box>
        {companies.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {companies.length > 1 ? 'Companies' : 'Company'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {companies.map((c) => {
                const rolesLabel = (c.roles || []).length ? ` — ${(c.roles || []).join(', ')}` : '';
                const inactiveLabel = c.is_active === false ? ' (inactive)' : '';
                return (
                  <Chip
                    key={c.id}
                    size="small"
                    variant={c.is_active === false ? 'outlined' : 'filled'}
                    color={c.is_active === false ? 'default' : 'primary'}
                    label={`${c.name}${rolesLabel}${inactiveLabel}`}
                    sx={c.is_active === false ? { opacity: 0.75 } : undefined}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const emptyOwner = {
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  password: '',
  phone: '',
};

const hasAnyOwnerField = (form) => {
  const { first_name, last_name, email, username, password, phone } = form;
  return [first_name, last_name, email, username, password, phone].some((v) => String(v || '').trim());
};

const isOwnerComplete = (form) => {
  const { first_name, last_name, email, username, password } = form;
  return [first_name, last_name, email, username, password].every((v) => String(v || '').trim());
};

const CompanyLogoAvatar = ({ company, isDarkMode, size = 64 }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = company.logo_download_url;
  const showLogo = logoUrl && !logoFailed;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        overflow: 'hidden',
        background: showLogo
          ? (isDarkMode ? 'rgba(255, 255, 255, 0.95)' : '#ffffff')
          : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: isDarkMode
          ? (showLogo ? '1px solid #334155' : '1px solid rgba(59, 130, 246, 0.45)')
          : (showLogo ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'),
        boxShadow: isDarkMode
          ? '0 8px 24px rgba(15, 23, 42, 0.45)'
          : '0 8px 24px rgba(59, 130, 246, 0.3)',
      }}
    >
      {showLogo ? (
        <Box
          component="img"
          src={logoUrl}
          alt={`${company.name} logo`}
          onError={() => setLogoFailed(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: 0.5,
          }}
        />
      ) : (
        <BusinessIcon sx={{ fontSize: size * 0.5, color: 'white' }} />
      )}
    </Box>
  );
};

/**
 * Platform console shown to a super-admin who has not yet picked a company.
 * Lists companies, lets the super-admin create a company (optionally with its first owner),
 * manage members (full CRUD), add owners, and select a company to enter the normal app.
 */
const SuperAdminCompanyPicker = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const selectedCompanyId = useSelector(getSelectedCompanyId);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { glassCard, companyCardHover, dialogPaperSx, primaryCtaSx } = useCompanyPickerSurfaces();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ company_name: '', ...emptyOwner });
  const [createBusy, setCreateBusy] = useState(false);

  const [ownerOpen, setOwnerOpen] = useState(false);
  const [ownerCompany, setOwnerCompany] = useState(null);
  const [ownerForm, setOwnerForm] = useState(emptyOwner);
  const [ownerBusy, setOwnerBusy] = useState(false);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameCompany, setRenameCompany] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsCompany, setDetailsCompany] = useState(null);

  const [membersOpen, setMembersOpen] = useState(false);
  const [membersCompany, setMembersCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersFilter, setMembersFilter] = useState('');
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberForm, setMemberForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    username: '',
    password: '',
    phone: '',
    roles: ['admin'],
  });
  const [memberBusy, setMemberBusy] = useState(false);
  const [memberFormErrors, setMemberFormErrors] = useState(emptyMemberFormErrors);
  const [memberFormShowError, setMemberFormShowError] = useState('');
  const [addMemberMode, setAddMemberMode] = useState('attach'); // 'attach' | 'create'
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  const [attachRoles, setAttachRoles] = useState(['admin']);
  const [editingMember, setEditingMember] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [editBusy, setEditBusy] = useState(false);

  const [deactivatedOpen, setDeactivatedOpen] = useState(false);
  const [deactivatedMode, setDeactivatedMode] = useState('company'); // 'company' | 'all'
  const [deactivatedCompany, setDeactivatedCompany] = useState(null);
  const [deactivatedUsers, setDeactivatedUsers] = useState([]);
  const [deactivatedLoading, setDeactivatedLoading] = useState(false);
  const [activateBusyKey, setActivateBusyKey] = useState(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [platformConfig, setPlatformConfig] = useState({ enableQueryLogs: true });

  const titleGradient = isDarkMode
    ? 'linear-gradient(135deg, #f1f5f9 0%, #94a3b8 50%, #f1f5f9 100%)'
    : 'linear-gradient(135deg, #0f172a 0%, #334155 50%, #0f172a 100%)';

  const statChipSx = {
    px: 1.5,
    py: 2.5,
    height: 'auto',
    borderRadius: 2,
    fontWeight: 600,
    fontSize: '0.85rem',
    background: isDarkMode
      ? 'rgba(51, 65, 85, 0.65)'
      : 'rgba(59, 130, 246, 0.08)',
    color: isDarkMode ? theme.palette.text.primary : theme.palette.text.primary,
    border: isDarkMode
      ? `1px solid ${theme.palette.divider}`
      : '1px solid rgba(59, 130, 246, 0.15)',
    '& .MuiChip-icon': {
      color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
    },
  };

  const actionButtonSx = {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    px: 2.5,
    py: 1,
  };

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const loadCompanies = async () => {
    setLoading(true);
    const res = await GET(api.getCompanies);
    setLoading(false);
    if (res.status === 200) {
      setCompanies(res.data?.companies || []);
    } else {
      showToast('error', res.message || 'Failed to load companies.');
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (addMemberMode !== 'create') return;
    setMemberFormErrors(emptyMemberFormErrors);
    setMemberFormShowError('');
  }, [memberForm, addMemberMode]);

  const handleEnter = (company) => {
    dispatch(setSelectedCompany({ id: company.id, name: company.name }));
  };

  const handleLogout = async () => {
    await GET(api.logout);
    removeLocalAccessTokenAndUser();
    dispatch(clearSelectedCompany());
    dispatch(removeUser());
  };

  const handleCreateCompany = async () => {
    const companyName = createForm.company_name?.trim();
    if (!companyName) {
      showToast('error', 'Company name is required.');
      return;
    }
    const wantsOwner = hasAnyOwnerField(createForm);
    if (wantsOwner && !isOwnerComplete(createForm)) {
      showToast('error', 'Fill in all owner fields, or leave them empty to skip.');
      return;
    }
    setCreateBusy(true);
    const res = wantsOwner
      ? await POST(api.provisionCompany, {}, createForm)
      : await POST(api.createCompany, {}, { name: companyName });
    setCreateBusy(false);
    if (res.status === 201) {
      showToast(
        'success',
        wantsOwner
          ? `Company "${companyName}" created with its first owner.`
          : `Company "${companyName}" created.`,
      );
      setCreateOpen(false);
      setCreateForm({ company_name: '', ...emptyOwner });
      loadCompanies();
    } else {
      showToast('error', res.message || 'Failed to create company.');
    }
  };

  const openAddOwner = (company) => {
    setOwnerCompany(company);
    setOwnerForm(emptyOwner);
    setOwnerOpen(true);
  };

  const openRename = (company) => {
    setRenameCompany(company);
    setRenameName(company.name);
    setRenameOpen(true);
  };

  const openDetails = (company) => {
    setDetailsCompany(company);
    setDetailsOpen(true);
  };

  const openMembers = async (company) => {
    setMembersCompany(company);
    setMembersOpen(true);
    setMembersFilter('');
    setAddMemberMode('attach');
    setSelectedExistingUser(null);
    setAttachRoles(['admin']);
    setMemberForm({
      email: '',
      first_name: '',
      last_name: '',
      username: '',
      password: '',
      phone: '',
      roles: ['admin'],
    });
    setMemberFormErrors(emptyMemberFormErrors);
    setMemberFormShowError('');
    setEditingMember(null);
    setMembersLoading(true);
    setEligibleLoading(true);
    const [membersRes, eligibleRes] = await Promise.all([
      GET(`${api.getCompanyMembers}/${company.id}/members?status=active`),
      GET(`${api.getEligibleCompanyUsers}/${company.id}/eligible-users`),
    ]);
    setMembersLoading(false);
    setEligibleLoading(false);
    if (membersRes.status === 200) {
      setMembers(membersRes.data?.members || []);
    } else {
      showToast('error', membersRes.message || 'Failed to load members.');
      setMembers([]);
    }
    if (eligibleRes.status === 200) {
      setEligibleUsers(eligibleRes.data?.users || []);
    } else {
      setEligibleUsers([]);
    }
  };

  const reloadMembers = async () => {
    if (!membersCompany) return;
    const [membersRes, eligibleRes] = await Promise.all([
      GET(`${api.getCompanyMembers}/${membersCompany.id}/members?status=active`),
      GET(`${api.getEligibleCompanyUsers}/${membersCompany.id}/eligible-users`),
    ]);
    if (membersRes.status === 200) {
      setMembers(membersRes.data?.members || []);
    }
    if (eligibleRes.status === 200) {
      setEligibleUsers(eligibleRes.data?.users || []);
    }
  };

  const reloadDeactivated = async () => {
    if (deactivatedMode === 'all') {
      const res = await GET(api.getDeactivatedUsers);
      if (res.status === 200) {
        setDeactivatedUsers(res.data?.users || []);
      }
      return;
    }
    if (!deactivatedCompany) return;
    const res = await GET(
      `${api.getCompanyMembers}/${deactivatedCompany.id}/members?status=inactive`,
    );
    if (res.status === 200) {
      setDeactivatedUsers(res.data?.members || []);
    }
  };

  const openDeactivatedForCompany = async (company) => {
    setDeactivatedMode('company');
    setDeactivatedCompany(company);
    setDeactivatedOpen(true);
    setDeactivatedLoading(true);
    const res = await GET(`${api.getCompanyMembers}/${company.id}/members?status=inactive`);
    setDeactivatedLoading(false);
    if (res.status === 200) {
      setDeactivatedUsers(res.data?.members || []);
    } else {
      showToast('error', res.message || 'Failed to load deactivated users.');
      setDeactivatedUsers([]);
    }
  };

  const openAllDeactivated = async () => {
    setDeactivatedMode('all');
    setDeactivatedCompany(null);
    setDeactivatedOpen(true);
    setDeactivatedLoading(true);
    const res = await GET(api.getDeactivatedUsers);
    setDeactivatedLoading(false);
    if (res.status === 200) {
      setDeactivatedUsers(res.data?.users || []);
    } else {
      showToast('error', res.message || 'Failed to load deactivated users.');
      setDeactivatedUsers([]);
    }
  };

  const openPlatformSettings = async () => {
    setSettingsOpen(true);
    setSettingsLoading(true);
    const res = await GET(api.getPlatformConfig);
    setSettingsLoading(false);
    if (res.status === 200 && res.data?.config) {
      setPlatformConfig(res.data.config);
    } else {
      showToast('error', res.message || 'Failed to load platform settings.');
    }
  };

  const handleToggleQueryLogs = async (enabled) => {
    const previous = platformConfig.enableQueryLogs;
    setPlatformConfig((prev) => ({ ...prev, enableQueryLogs: enabled }));
    setSettingsBusy(true);
    const res = await PATCH(api.updatePlatformConfig, {}, { enableQueryLogs: enabled });
    setSettingsBusy(false);
    if (res.status === 200 && res.data?.config) {
      setPlatformConfig(res.data.config);
      showToast('success', enabled ? 'Query logs enabled.' : 'Query logs disabled.');
    } else {
      setPlatformConfig((prev) => ({ ...prev, enableQueryLogs: previous }));
      showToast('error', res.message || 'Failed to update platform settings.');
    }
  };

  const handleActivateMembership = async (userId, companyId) => {
    const busyKey = `${userId}:${companyId}`;
    setActivateBusyKey(busyKey);
    const res = await PATCH(
      `${api.updateCompanyMember}/${companyId}/members/${userId}`,
      {},
      { is_active: true },
    );
    setActivateBusyKey(null);
    if (res.status === 200) {
      showToast('success', 'Member activated.');
      await reloadDeactivated();
      loadCompanies();
      if (membersOpen) reloadMembers();
    } else {
      showToast('error', res.message || 'Failed to activate member.');
    }
  };

  const toggleMemberRole = (role, list, setList) => {
    if (list.includes(role)) {
      if (list.length === 1) return;
      setList(list.filter((r) => r !== role));
      return;
    }
    // Owner and general_manager are mutually exclusive (other roles may combine with either).
    let next = [...list, role];
    if (role === 'owner') {
      next = next.filter((r) => r !== 'general_manager');
    } else if (role === 'general_manager') {
      next = next.filter((r) => r !== 'owner');
    }
    setList(next);
  };

  const handleAttachExistingUser = async () => {
    if (!selectedExistingUser?.email) {
      showToast('error', 'Select a user to attach.');
      return;
    }
    if (!attachRoles.length) {
      showToast('error', 'Select at least one role.');
      return;
    }
    setMemberBusy(true);
    const res = await POST(`${api.addCompanyMember}/${membersCompany.id}/members`, {}, {
      email: selectedExistingUser.email,
      roles: attachRoles,
    });
    setMemberBusy(false);
    if (res.status === 201) {
      showToast('success', `${selectedExistingUser.email} attached to the company.`);
      setSelectedExistingUser(null);
      setAttachRoles(['admin']);
      reloadMembers();
      loadCompanies();
    } else {
      showToast('error', res.message || 'Failed to attach user.');
    }
  };

  const handleCreateNewMember = async () => {
    setMemberFormShowError('');
    setMemberFormErrors(emptyMemberFormErrors);

    const validationErrors = validateNewMemberForm(memberForm);
    if (Object.keys(validationErrors).length > 0) {
      setMemberFormErrors(validationErrors);
      return;
    }

    const email = memberForm.email.trim();
    setMemberBusy(true);
    const res = await POST(`${api.addCompanyMember}/${membersCompany.id}/members`, {}, {
      email,
      roles: memberForm.roles,
      first_name: memberForm.first_name.trim(),
      last_name: memberForm.last_name.trim(),
      username: memberForm.username.trim(),
      password: memberForm.password,
      phone: memberForm.phone || undefined,
    });
    setMemberBusy(false);
    if (res.status === 201) {
      showToast('success', 'New user created and added to the company.');
      setMemberForm({
        email: '',
        first_name: '',
        last_name: '',
        username: '',
        password: '',
        phone: '',
        roles: ['admin'],
      });
      setMemberFormErrors(emptyMemberFormErrors);
      setMemberFormShowError('');
      reloadMembers();
      loadCompanies();
    } else if (res.status === 409) {
      setMemberFormShowError(res.message || 'A user with this email already exists.');
    } else {
      setMemberFormShowError(res.message || res.data?.message || 'Failed to create user.');
    }
  };

  const handleUpdateMemberRoles = async () => {
    if (!editingMember || !editRoles.length) return;
    setEditBusy(true);
    const res = await PATCH(
      `${api.updateCompanyMember}/${membersCompany.id}/members/${editingMember.id}`,
      {},
      { roles: editRoles },
    );
    setEditBusy(false);
    if (res.status === 200) {
      showToast('success', 'Member roles updated.');
      setEditingMember(null);
      reloadMembers();
    } else {
      showToast('error', res.message || 'Failed to update member.');
    }
  };

  const handleToggleMemberActive = async (member) => {
    const currentlyActive = member.membership_is_active !== false;
    const nextActive = !currentlyActive;
    const res = await PATCH(
      `${api.updateCompanyMember}/${membersCompany.id}/members/${member.id}`,
      {},
      { is_active: nextActive },
    );
    if (res.status === 200) {
      const updated = res.data?.member;
      setMembers((prev) => prev.map((m) => (
        m.id === member.id
          ? {
            ...m,
            ...(updated || {}),
            membership_is_active: updated?.membership_is_active ?? nextActive,
          }
          : m
      )));
      showToast(
        'success',
        nextActive
          ? 'Member activated for this company.'
          : (updated?.account_deactivated
            ? 'Member deactivated (account has no other active companies).'
            : 'Member deactivated for this company.'),
      );
      reloadMembers();
      loadCompanies();
    } else {
      showToast('error', res.message || 'Failed to update member.');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.email} from ${membersCompany.name}?`)) return;
    const res = await DELETE(`${api.removeCompanyMember}/${membersCompany.id}/members/${member.id}`);
    if (res.status === 200) {
      showToast('success', 'Member removed.');
      reloadMembers();
      loadCompanies();
    } else {
      showToast('error', res.message || 'Failed to remove member.');
    }
  };

  const filteredMembers = members.filter((m) => matchesUserTextFilter(m, membersFilter));

  const handleRenameCompany = async () => {
    const name = renameName.trim();
    if (!name) {
      showToast('error', 'Company name is required.');
      return;
    }
    setRenameBusy(true);
    const res = await PATCH(`${api.updateCompany}/${renameCompany.id}`, {}, { name });
    setRenameBusy(false);
    if (res.status === 200) {
      const updatedName = res.data?.company?.name || name;
      showToast('success', `Company renamed to "${updatedName}".`);
      setRenameOpen(false);
      setCompanies((prev) => prev.map((c) => (
        c.id === renameCompany.id ? { ...c, name: updatedName } : c
      )));
      if (selectedCompanyId === renameCompany.id) {
        dispatch(setSelectedCompany({ id: renameCompany.id, name: updatedName }));
      }
    } else {
      showToast('error', res.message || 'Failed to rename company.');
    }
  };

  const handleAddOwner = async () => {
    const { first_name, last_name, email, username, password } = ownerForm;
    if (!first_name || !last_name || !email || !username || !password) {
      showToast('error', 'All owner fields are required.');
      return;
    }
    setOwnerBusy(true);
    const res = await POST(`${api.addCompanyOwner}/${ownerCompany.id}/owners`, {}, ownerForm);
    setOwnerBusy(false);
    if (res.status === 201) {
      showToast('success', `Owner added to "${ownerCompany.name}".`);
      setOwnerOpen(false);
    } else {
      showToast('error', res.message || 'Failed to add owner.');
    }
  };

  const ownerFields = (form, setForm) => (
    <Box component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <input
        type="text"
        tabIndex={-1}
        autoComplete="username"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />
      <input
        type="password"
        tabIndex={-1}
        autoComplete="current-password"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={6}>
          <TextField label="First name" fullWidth value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            slotProps={{ htmlInput: { autoComplete: 'off' } }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Last name" fullWidth value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            slotProps={{ htmlInput: { autoComplete: 'off' } }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Email" type="email" fullWidth value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            slotProps={{ htmlInput: { autoComplete: 'off' } }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Username" fullWidth value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            slotProps={{ htmlInput: { autoComplete: 'off', name: 'company-owner-username' } }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Password" type="password" fullWidth value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            slotProps={{ htmlInput: { autoComplete: 'new-password', name: 'company-owner-password' } }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Phone (optional)" fullWidth value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            slotProps={{ htmlInput: { autoComplete: 'off' } }} />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CompanyPickerAmbientBg />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Fade in timeout={600}>
          <Box
            sx={{
              ...glassCard,
              p: { xs: 2.5, md: 3.5 },
              mb: 4,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box
                sx={{
                  width: isMobile ? 56 : 72,
                  height: isMobile ? 56 : 72,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.45)' : 'none',
                  boxShadow: isDarkMode
                    ? '0 12px 40px rgba(59, 130, 246, 0.35)'
                    : '0 12px 40px rgba(59, 130, 246, 0.35)',
                  animation: 'companyPickerIconPulse 3s ease-in-out infinite',
                  flexShrink: 0,
                }}
              >
                <BusinessIcon sx={{ fontSize: isMobile ? 28 : 36, color: 'white' }} />
              </Box>
              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{
                    fontWeight: 800,
                    background: titleGradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '200% auto',
                    animation: 'companyPickerShimmer 3s linear infinite',
                    letterSpacing: '-0.02em',
                    mb: 0.5,
                  }}
                >
                  Platform Console
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                >
                  Signed in as {user.email} · Select a company to manage
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Tooltip title={isDarkMode ? 'Light mode' : 'Dark mode'}>
                <IconButton
                  onClick={() => dispatch(toggleTheme())}
                  sx={{
                    color: 'text.primary',
                    background: isDarkMode ? 'rgba(51, 65, 85, 0.55)' : 'rgba(0, 0, 0, 0.04)',
                    border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      background: isDarkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={openPlatformSettings}
                sx={{
                  ...actionButtonSx,
                  color: 'text.primary',
                  borderColor: isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.15)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.55)' : 'rgba(0, 0, 0, 0.3)',
                    background: isDarkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {isMobile ? 'Settings' : 'Platform settings'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PersonOffIcon />}
                onClick={openAllDeactivated}
                sx={{
                  ...actionButtonSx,
                  color: 'text.primary',
                  borderColor: isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.15)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.55)' : 'rgba(0, 0, 0, 0.3)',
                    background: isDarkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {isMobile ? 'Deactivated' : 'Deactivated users'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{
                  ...actionButtonSx,
                  ...primaryCtaSx,
                  whiteSpace: 'nowrap',
                }}
              >
                New Company
              </Button>
              <UserAvatar
                user={user}
                size={40}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  boxShadow: isDarkMode
                    ? '0 4px 12px rgba(59, 130, 246, 0.35)'
                    : '0 4px 12px rgba(59, 130, 246, 0.25)',
                }}
              />
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  ...actionButtonSx,
                  color: 'text.primary',
                  borderColor: isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.15)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.55)' : 'rgba(0, 0, 0, 0.3)',
                    background: isDarkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Log out
              </Button>
            </Box>
          </Box>
        </Fade>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 2 }}>
            <CircularProgress sx={{ color: 'primary.main' }} />
            <Typography sx={{ color: 'text.secondary' }}>
              Loading companies…
            </Typography>
          </Box>
        ) : companies.length === 0 ? (
          <Grow in timeout={800}>
            <Box
              sx={{
                ...glassCard,
                p: { xs: 5, md: 8 },
                textAlign: 'center',
                maxWidth: 560,
                mx: 'auto',
              }}
            >
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: '50%',
                  background: isDarkMode
                    ? 'rgba(59, 130, 246, 0.15)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: isDarkMode
                    ? '1px solid rgba(59, 130, 246, 0.35)'
                    : '1px solid rgba(59, 130, 246, 0.2)',
                }}
              >
                <BusinessIcon sx={{ fontSize: 44, color: 'primary.main' }} />
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}
              >
                No companies yet
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, color: 'text.secondary' }}
              >
                Create your first company to get started.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{
                  ...actionButtonSx,
                  px: 4,
                  ...primaryCtaSx,
                }}
              >
                New Company
              </Button>
            </Box>
          </Grow>
        ) : (
          <Grid container spacing={3}>
            {companies.map((company, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={company.id}>
                <Fade in timeout={600} style={{ transitionDelay: `${index * 80}ms` }}>
                  <Box
                    sx={{
                      ...glassCard,
                      ...companyCardHover,
                      p: { xs: 3, md: 4 },
                      minHeight: 280,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3 }}>
                      <CompanyLogoAvatar company={company} isDarkMode={isDarkMode} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: 'text.primary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {company.name}
                          </Typography>
                          <Tooltip title="Rename company">
                            <IconButton
                              size="small"
                              onClick={() => openRename(company)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: 'primary.main',
                                  background: isDarkMode ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit company profile">
                            <IconButton
                              size="small"
                              onClick={() => openDetails(company)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: 'primary.main',
                                  background: isDarkMode ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
                                },
                              }}
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          Company workspace
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3, flexGrow: 1 }}>
                      <Chip
                        icon={<PeopleIcon />}
                        label={`${company.user_count ?? 0} users`}
                        sx={statChipSx}
                      />
                      <Chip
                        icon={<GroupsIcon />}
                        label={`${company.client_count ?? 0} clients`}
                        sx={statChipSx}
                      />
                      <Chip
                        icon={<WorkIcon />}
                        label={`${company.job_count ?? 0} jobs`}
                        sx={statChipSx}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          startIcon={<PeopleIcon />}
                          onClick={() => openMembers(company)}
                          sx={{
                            ...actionButtonSx,
                            color: 'text.primary',
                            background: isDarkMode ? 'rgba(51, 65, 85, 0.55)' : 'rgba(0, 0, 0, 0.04)',
                            border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              background: isDarkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(0, 0, 0, 0.08)',
                            },
                          }}
                        >
                          Members
                        </Button>
                        <Button
                          startIcon={<PersonOffIcon />}
                          onClick={() => openDeactivatedForCompany(company)}
                          sx={{
                            ...actionButtonSx,
                            color: 'text.primary',
                            background: isDarkMode ? 'rgba(51, 65, 85, 0.55)' : 'rgba(0, 0, 0, 0.04)',
                            border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              background: isDarkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(0, 0, 0, 0.08)',
                            },
                          }}
                        >
                          Deactivated
                        </Button>
                        <Button
                          startIcon={<PersonAddIcon />}
                          onClick={() => openAddOwner(company)}
                          sx={{
                            ...actionButtonSx,
                            color: 'text.primary',
                            background: isDarkMode ? 'rgba(51, 65, 85, 0.55)' : 'rgba(0, 0, 0, 0.04)',
                            border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              background: isDarkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(0, 0, 0, 0.08)',
                            },
                          }}
                        >
                          Add owner
                        </Button>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<LoginIcon />}
                        onClick={() => handleEnter(company)}
                        sx={{
                          ...actionButtonSx,
                          flex: { xs: '1 1 100%', sm: '0 0 auto' },
                          ...primaryCtaSx,
                        }}
                      >
                        Enter
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Create company + first owner */}
      <Dialog
        open={createOpen}
        onClose={() => !createBusy && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Create Company</DialogTitle>
        <DialogContent>
          <TextField label="Company name" fullWidth sx={{ mt: 1 }} value={createForm.company_name}
            onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })} />
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>First owner (optional)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Leave blank to create the company now and add an owner later from the company card.
          </Typography>
          {ownerFields(createForm, setCreateForm)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={createBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCompany} disabled={createBusy}>
            {createBusy ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename company */}
      <Dialog
        open={renameOpen}
        onClose={() => !renameBusy && setRenameOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Rename company{renameCompany ? ` — ${renameCompany.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Company name"
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !renameBusy) handleRenameCompany();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRenameOpen(false)} disabled={renameBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleRenameCompany} disabled={renameBusy}>
            {renameBusy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add owner to existing company */}
      <Dialog
        open={ownerOpen}
        onClose={() => !ownerBusy && setOwnerOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add owner{ownerCompany ? ` to ${ownerCompany.name}` : ''}</DialogTitle>
        <DialogContent>
          {ownerFields(ownerForm, setOwnerForm)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOwnerOpen(false)} disabled={ownerBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleAddOwner} disabled={ownerBusy}>
            {ownerBusy ? 'Adding…' : 'Add owner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company profile / logo / attachments */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Company profile{detailsCompany ? ` — ${detailsCompany.name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailsCompany && (
            process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
              <LoadScript
                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                libraries={GOOGLE_MAPS_LIBRARIES}
              >
                <CompanyProfileSection
                  mode="platform"
                  companyId={detailsCompany.id}
                  user={user}
                  onSaved={(msg) => {
                    setToast({ open: true, severity: 'success', message: msg });
                    loadCompanies();
                  }}
                  onError={(msg) => setToast({ open: true, severity: 'error', message: msg })}
                />
              </LoadScript>
            ) : (
              <CompanyProfileSection
                mode="platform"
                companyId={detailsCompany.id}
                user={user}
                onSaved={(msg) => {
                  setToast({ open: true, severity: 'success', message: msg });
                  loadCompanies();
                }}
                onError={(msg) => setToast({ open: true, severity: 'error', message: msg })}
              />
            )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manage company members */}
      <Dialog
        open={membersOpen}
        onClose={() => {
          if (memberBusy || editBusy) return;
          setMembersOpen(false);
          setMembersFilter('');
        }}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Members{membersCompany ? ` — ${membersCompany.name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                size="small"
                fullWidth
                label="Filter members"
                placeholder="Search by name, username, or email"
                value={membersFilter}
                onChange={(e) => setMembersFilter(e.target.value)}
              />
              {filteredMembers.map((m) => {
                const isEditing = editingMember?.id === m.id;
                return (
                  <Box
                    key={m.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: isDarkMode
                        ? `1px solid ${isEditing ? theme.palette.primary.main : theme.palette.divider}`
                        : `1px solid ${isEditing ? theme.palette.primary.main : '#e2e8f0'}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1.5,
                        alignItems: { sm: 'center' },
                        justifyContent: 'space-between',
                      }}
                    >
                      <MemberInfoBox
                        member={m}
                        showCurrentCompanyRoles={!isEditing}
                        companyIdForMembershipChip={membersCompany?.id}
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant={isEditing ? 'outlined' : 'text'}
                          onClick={() => {
                            if (isEditing) {
                              setEditingMember(null);
                              return;
                            }
                            setEditingMember(m);
                            setEditRoles([...(m.roles || [])]);
                          }}
                          disabled={editBusy}
                        >
                          {isEditing ? 'Cancel' : 'Edit roles'}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleToggleMemberActive(m)}
                          disabled={isEditing && editBusy}
                        >
                          Deactivate
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(m)}
                          disabled={isEditing && editBusy}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {isEditing && (
                      <Box
                        sx={{
                          pt: 1.5,
                          borderTop: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Roles in this company
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                          {COMPANY_ROLE_OPTIONS.map((role) => (
                            <Chip
                              key={role}
                              label={role}
                              color={editRoles.includes(role) ? 'primary' : 'default'}
                              onClick={() => toggleMemberRole(role, editRoles, setEditRoles)}
                              variant={editRoles.includes(role) ? 'filled' : 'outlined'}
                              disabled={editBusy}
                            />
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => setEditingMember(null)}
                            disabled={editBusy}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleUpdateMemberRoles}
                            disabled={editBusy || !editRoles.length}
                          >
                            {editBusy ? 'Saving…' : 'Save roles'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
              {filteredMembers.length === 0 && (
                <Typography color="text.secondary">
                  {members.length === 0 ? 'No members yet.' : 'No members match.'}
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Add member
          </Typography>
          <Tabs
            value={addMemberMode}
            onChange={(_, value) => setAddMemberMode(value)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="attach" label="Attach existing user" />
            <Tab value="create" label="Create new user" />
          </Tabs>

          {addMemberMode === 'attach' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Search and select a user who already exists on the platform, then choose their roles in this company.
              </Typography>
              <Autocomplete
                options={eligibleUsers}
                loading={eligibleLoading}
                value={selectedExistingUser}
                onChange={(_, value) => setSelectedExistingUser(value)}
                getOptionLabel={(option) =>
                  option
                    ? `${option.first_name || ''} ${option.last_name || ''}`.trim()
                      + (option.email ? ` (${option.email})` : '')
                      + (option.username ? ` · @${option.username}` : '')
                    : ''
                }
                filterOptions={(options, { inputValue }) =>
                  options.filter((u) => matchesUserTextFilter(u, inputValue))
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <UserAvatar
                        user={option}
                        size={32}
                        sx={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          color: 'white',
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.first_name} {option.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}{option.username ? ` · @${option.username}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search users"
                    placeholder="Filter by name, email, or username"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {eligibleLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={eligibleLoading ? 'Loading…' : 'No eligible users found'}
              />
              <Box>
                <Typography variant="caption" color="text.secondary">Roles in this company</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {COMPANY_ROLE_OPTIONS.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      color={attachRoles.includes(role) ? 'primary' : 'default'}
                      onClick={() => toggleMemberRole(role, attachRoles, setAttachRoles)}
                      variant={attachRoles.includes(role) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleAttachExistingUser}
                  disabled={memberBusy || !selectedExistingUser}
                >
                  {memberBusy ? 'Attaching…' : 'Attach to company'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Create a brand-new account and add them to this company in one step.
              </Typography>
              {memberFormShowError && (
                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                  {memberFormShowError}
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    required
                    fullWidth
                    value={memberForm.email}
                    onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                    error={!!memberFormErrors.email}
                    helperText={memberFormErrors.email}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Username"
                    required
                    fullWidth
                    value={memberForm.username}
                    onChange={(e) => setMemberForm({ ...memberForm, username: e.target.value })}
                    error={!!memberFormErrors.username}
                    helperText={memberFormErrors.username || '3-50 characters, alphanumeric and underscores only'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First name"
                    required
                    fullWidth
                    value={memberForm.first_name}
                    onChange={(e) => setMemberForm({ ...memberForm, first_name: e.target.value })}
                    error={!!memberFormErrors.first_name}
                    helperText={memberFormErrors.first_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last name"
                    required
                    fullWidth
                    value={memberForm.last_name}
                    onChange={(e) => setMemberForm({ ...memberForm, last_name: e.target.value })}
                    error={!!memberFormErrors.last_name}
                    helperText={memberFormErrors.last_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Password"
                    type="password"
                    required
                    fullWidth
                    value={memberForm.password}
                    onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                    error={!!memberFormErrors.password}
                    helperText={memberFormErrors.password || 'Minimum 6 characters'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone (optional)"
                    fullWidth
                    value={memberForm.phone}
                    onChange={(e) => setMemberForm({
                      ...memberForm,
                      phone: formatPhoneNumber(e.target.value),
                    })}
                    error={!!memberFormErrors.phone}
                    helperText={memberFormErrors.phone}
                    placeholder="(555) 123-4567"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Roles in this company</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {COMPANY_ROLE_OPTIONS.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        color={memberForm.roles.includes(role) ? 'primary' : 'default'}
                        onClick={() => toggleMemberRole(role, memberForm.roles, (next) => setMemberForm({ ...memberForm, roles: next }))}
                        variant={memberForm.roles.includes(role) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                  {memberFormErrors.roles && (
                    <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                      {memberFormErrors.roles}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleCreateNewMember} disabled={memberBusy}>
                  {memberBusy ? 'Creating…' : 'Create and add'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setMembersOpen(false);
              setMembersFilter('');
            }}
            disabled={memberBusy}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivated users (per-company or platform-wide) */}
      <Dialog
        open={deactivatedOpen}
        onClose={() => !activateBusyKey && setDeactivatedOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {deactivatedMode === 'all'
            ? 'Deactivated users — all companies'
            : `Deactivated users${deactivatedCompany ? ` — ${deactivatedCompany.name}` : ''}`}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Inactive in company</strong> — cannot enter that company; can still log in if another membership is active.
            </Typography>
            <Typography variant="body2">
              <strong>Account inactive</strong> — login blocked globally (usually the last active membership was deactivated).
            </Typography>
          </Alert>

          {deactivatedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {deactivatedUsers.map((u) => {
                const companies = normalizeCompanies(u.companies);
                const inactiveMemberships = companies.filter((c) => c.is_active === false);
                const activateTargets = deactivatedMode === 'company' && deactivatedCompany
                  ? [{ id: deactivatedCompany.id, name: deactivatedCompany.name }]
                  : (inactiveMemberships.length > 0
                    ? inactiveMemberships
                    : (u.home_company_id
                      ? companies.filter((c) => c.id === u.home_company_id)
                      : companies.slice(0, 1)));

                return (
                  <Box
                    key={u.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid #e2e8f0',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5,
                      alignItems: { sm: 'flex-start' },
                      justifyContent: 'space-between',
                      opacity: 0.95,
                    }}
                  >
                    <MemberInfoBox
                      member={u}
                      showCurrentCompanyRoles={deactivatedMode === 'company'}
                      companyIdForMembershipChip={
                        deactivatedMode === 'company' ? deactivatedCompany?.id : null
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                      {activateTargets.map((c) => {
                        const busyKey = `${u.id}:${c.id}`;
                        return (
                          <Button
                            key={c.id}
                            size="small"
                            variant="contained"
                            disabled={!!activateBusyKey}
                            onClick={() => handleActivateMembership(u.id, c.id)}
                          >
                            {activateBusyKey === busyKey
                              ? 'Activating…'
                              : deactivatedMode === 'all'
                                ? `Activate in ${c.name}`
                                : 'Activate'}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
              {deactivatedUsers.length === 0 && (
                <Typography color="text.secondary">No deactivated users found.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeactivatedOpen(false)} disabled={!!activateBusyKey}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={settingsOpen}
        onClose={() => !settingsBusy && setSettingsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Platform settings</DialogTitle>
        <DialogContent>
          {settingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Runtime toggles for this API process. Changes apply immediately and reset to defaults on restart.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!platformConfig.enableQueryLogs}
                    onChange={(e) => handleToggleQueryLogs(e.target.checked)}
                    disabled={settingsBusy}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Enable query logs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Log successful SQL queries to the API console
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0, gap: 1 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSettingsOpen(false)} disabled={settingsBusy}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminCompanyPicker;
